import React, { useCallback, useContext, useEffect, useReducer } from "react"
import { Platform } from "react-native"
import Animated, { Easing, useAnimatedStyle } from "react-native-reanimated"

import styled from "styled-components/native"

import { useLockBodyScroll } from "../hooks/use-lock-body-scroll"
import { useOpenAnimation } from "../hooks/use-open-animation"
import { useProfiles } from "../hooks/use-profiles"
import { audioPlayer } from "../lib/audio-player"
import { cancelPlayContentAudio } from "../lib/content-audio"
import { createStyle } from "../lib/create-style"
import { ebookReader } from "../lib/ebook-reader"
import {
  ActiveProfileContext,
  ProfilesOverlayEvent,
  ProfilesOverlayScreen,
  profilesOverlay,
} from "../lib/profiles"
import { KeyboardAwareScrollView } from "./keyboard-controller"
import { Portal } from "./portal"
import { ProfilesAddScreen } from "./profiles-add-screen"
import { ProfilesEditScreen } from "./profiles-edit-screen"
import { ProfilesManageScreen } from "./profiles-manage-screen"
import { ProfilesSelectScreen } from "./profiles-select-screen"
import { SystemBars } from "./system-bars"
import { useTokens } from "./tokens-provider"

const SCREEN_FADE_DURATION_MS = 125

const scrollViewContentContainerStyle = createStyle({
  flexGrow: 1,
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "center",
})

const Container = styled(Animated.View)`
  position: ${Platform.OS === "web" ? "fixed" : "absolute"};
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.background.primary};
`

const Content = styled(Animated.View)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-vertical: ${(props) => props.theme.spacing.large}px;
`

// A reducer to juggle screen transitions for the profiles overlay
const reducer = (
  state: {
    open: boolean
    previousScreens: ProfilesOverlayScreen[]
    screen: ProfilesOverlayScreen | undefined
    nextScreen: ProfilesOverlayScreen | undefined
  },
  action:
    | { type: "show"; screen: ProfilesOverlayScreen }
    | { type: "push"; screen: ProfilesOverlayScreen }
    | { type: "pop" }
    | { type: "close" },
) => {
  switch (action.type) {
    case "show":
      return {
        ...state,
        open: true,
        screen: action.screen,
        nextScreen: undefined,
      }
    case "push":
      return {
        ...state,
        previousScreens: state.screen
          ? [...state.previousScreens, state.screen]
          : state.previousScreens,
        nextScreen: action.screen,
      }
    case "pop":
      if (state.open && state.previousScreens.length) {
        return {
          ...state,
          previousScreens: state.previousScreens.slice(0, -1),
          nextScreen: state.previousScreens[state.previousScreens.length - 1],
        }
      } else {
        return { ...state, open: false }
      }
    case "close":
      return { ...state, open: false }
  }
}

export function ProfilesOverlay() {
  const activeProfile = useContext(ActiveProfileContext)
  const profiles = useProfiles()
  const [{ open, screen, nextScreen }, dispatch] = useReducer(reducer, {
    previousScreens: [],
    open: false,
    screen: undefined,
    nextScreen: undefined,
  })
  const { displayMode } = useTokens()

  const onCompleteContentAnimation = useCallback(
    (open: boolean) => {
      if (!open && nextScreen) {
        dispatch({ type: "show", screen: nextScreen })
      }
    },
    [nextScreen],
  )

  const [overlayAnimation, overlayState] = useOpenAnimation({
    open,
  })

  const [screenAnimation] = useOpenAnimation({
    open: screen !== undefined && nextScreen === undefined,
    duration: SCREEN_FADE_DURATION_MS,
    easing: Easing.linear,
    onComplete: onCompleteContentAnimation,
  })

  const onPressBack = useCallback(() => {
    if (open) {
      dispatch({ type: "pop" })
      // Press handled, don't bubble up to any other handlers
      return true
    }
  }, [open])

  useEffect(
    () =>
      profilesOverlay.on(ProfilesOverlayEvent.RequestOpen, (event) => {
        if (profiles.data?.status === "available") {
          dispatch({
            type: open ? "push" : "show",
            screen: event.screen,
          })
        }
      }),
    [open, profiles.data],
  )

  // Listen for a request to close the overlay
  useEffect(() =>
    profilesOverlay.on(ProfilesOverlayEvent.RequestClose, () =>
      dispatch({ type: "close" }),
    ),
  )

  // Listen for a request to go back to the previous screen
  useEffect(
    () =>
      profilesOverlay.on(ProfilesOverlayEvent.RequestBack, () =>
        dispatch({ type: "pop" }),
      ),
    [],
  )

  // Keep the profilesOverlay instance apprised of the current state
  useEffect(() => {
    void profilesOverlay.setState({ open })
  }, [open])

  // Close the ebook reader (if it's open) after the profiles overlay finishes
  // animating in (waiting until after the profiles overlay has animated results
  // in a smoother experience visually).
  useEffect(() => {
    if (overlayState === "open") {
      ebookReader.requestClose()
    }
  }, [overlayState])

  // Pause the audio player as soon as the profiles overlay starts opening
  useEffect(() => {
    if (open) {
      cancelPlayContentAudio()
      void audioPlayer.pause()
    }
  }, [open])

  // Lock body scroll when we're open
  useLockBodyScroll(open)

  // If the user has multiple profiles, they haven't selected one on this
  // device, and the overlay isn't already open, then show the profiles select
  // screen
  useEffect(() => {
    if (
      activeProfile.state === "unset" &&
      profiles.data?.profiles.length &&
      !open
    ) {
      dispatch({ type: "show", screen: { name: "select" } })
    }
  }, [activeProfile.state, profiles.data, open])

  // Close the profiles overlay if the browser forward or back button is used to
  // navigate away from the page where the overlay was opened
  useEffect(() => {
    if (Platform.OS === "web" && open) {
      const handleState = () => {
        dispatch({ type: "close" })
      }
      window.addEventListener("popstate", handleState)
      return () => window.removeEventListener("popstate", handleState)
    }
  }, [open])

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: overlayAnimation.value,
  }))
  const screenStyle = useAnimatedStyle(() => ({
    opacity: screenAnimation.value,
  }))

  return (
    <Portal>
      {screen && overlayState !== "closed" ? (
        <Container style={overlayStyle}>
          <KeyboardAwareScrollView
            contentContainerStyle={scrollViewContentContainerStyle}
          >
            <Content style={screenStyle}>
              {screen.name === "select" ? (
                <ProfilesSelectScreen />
              ) : screen.name === "add" ? (
                <ProfilesAddScreen />
              ) : screen.name === "edit" ? (
                <ProfilesEditScreen profileId={screen.params?.profileId} />
              ) : (
                <ProfilesManageScreen />
              )}
            </Content>
          </KeyboardAwareScrollView>
          <SystemBars
            style={{ statusBar: displayMode }}
            onPressBack={onPressBack}
          />
        </Container>
      ) : undefined}
    </Portal>
  )
}
